import type { LocalizedText } from "@packages/core/language/language.ts";

export const en = {
    Navigation: {
        "review-cycle-detail-settings": {
            label: "Review cycle",
            main: {
                label: "Review cycle",
                progression: { label: "Progression" },
                scores: { label: "Scores" },
                calibration: { label: "Calibration" },
                questions: { label: "Question results" },
                themes: { label: "Themes" }
            }
        },
        "review-cycle-detail-home": {
            label: "Review cycle",
            main: {
                label: "Review cycle",
                progression: { label: "Progression" },
                scores: { label: "Scores" },
                calibration: { label: "Calibration" },
                questions: { label: "Question results" },
                themes: { label: "Themes" }
            }
        },
        "review-cycle-builder-settings": { label: "Edit review cycle" },
        "review-cycle-builder-home": { label: "Edit review cycle" },
        "review-cycle-new-settings": { label: "New review cycle" },
        "review-cycle-new-home": { label: "New review cycle" },
        "job-architecture-ai-setup": {
            label: "Set up your job architecture"
        },
        "admin-settings-job": { label: "Job" },
        "admin-settings": {
            label: "Admin",
            organization: {
                label: "Organization",
                company: {
                    label: "Company",
                    profile: { label: "Profile" },
                    "mission-and-values": { label: "Mission & values" }
                },
                people: {
                    label: "People",
                    "manage-people": { label: "Manage people" },
                    "people-fields": { label: "Employee fields" },
                    jobs: { label: "Jobs" },
                    "jobs-get-started": {
                        label: "Set up your job architecture"
                    },
                    "job-architecture": { label: "Job Architecture" },
                    "jobs-architecture-ai": { label: "Set up with AI" },
                    "job-new": { label: "New job" },
                    segments: {
                        label: "Segments",
                        mine: { label: "My segments" },
                        "shared-with-me": { label: "Shared with me" }
                    },
                    "segment-new": { label: "New segment" },
                    "segment-edit": { label: "Segment" },
                    "manage-people-detail": {
                        label: "Person",
                        details: { label: "Details" },
                        job: { label: "Job" },
                        permissions: { label: "Permissions" }
                    }
                },
                "roles-and-permissions": { label: "Roles & permissions" },
                integrations: { label: "Integrations" },
                billing: { label: "Billing" },
                features: { label: "Features" }
            },
            agent: {
                label: "Agent",
                general: { label: "General" },
                "skill-library": { label: "Skills" },
                "skill-detail": { label: "Skill" },
                "connected-apps": {
                    label: "Connected apps",
                    "knowledge-sources": { label: "Knowledge sources" },
                    access: { label: "Access" }
                }
            },
            features: {
                label: "Features",
                engagement: {
                    label: "Engagement",
                    settings: { label: "Settings" },
                    access: { label: "Access" }
                },
                goals: { label: "Goals" },
                "good-vibes": {
                    label: "Good Vibes",
                    settings: { label: "Settings" },
                    access: { label: "Access" }
                },
                meetings: { label: "Meetings" },
                "performance-reviews": {
                    label: "Performance reviews",
                    "review-cycles": { label: "Review cycles" },
                    access: { label: "Access" }
                },
                plans: { label: "Plans" }
            }
        }
    },
    CompanyPage: {
        loading: "Loading company info",
        loadError: "We couldn't load your company info.",
        retry: "Try again",
        savedConfirmation: "Changes saved",
        saveErrorToast: "We couldn't save your changes. Please try again.",
        discard: "Discard",
        saveChanges: "Save changes",
        logo: {
            label: "Logo",
            description: "PNG, JPG, or GIF. 5 MB max.",
            alt: "Company logo",
            uploadButton: "Upload logo",
            uploadedToast: "Logo updated",
            error: {
                INVALID_IMAGE_FORMAT: "That file isn't a supported image. Use a PNG, JPG, or GIF.",
                IMAGE_TOO_LARGE: "That image is too large. Use a file under 5 MB.",
                generic: "We couldn't upload your logo. Please try again."
            }
        },
        name: {
            label: "Company name",
            description: "The name your team sees across Workleap.",
            placeholder: "Your company name",
            required: "Company name can't be empty."
        },
        timeZone: {
            label: "Timezone",
            description: "Employees can override this in their preferences.",
            placeholder: "Search for a timezone…"
        },
        language: {
            label: "Default language",
            description: "Employees can override this in their preferences.",
            placeholder: "Select a language",
            options: {
                "en-US": "English (US)",
                "fr-CA": "French (Canada)"
            } satisfies LocalizedText
        },
        mission: {
            label: "Mission",
            description: "A short statement of what your company sets out to do.",
            placeholder: "Describe your company's mission…"
        },
        values: {
            label: "Company values",
            description: "The principles your team is expected to work by. Drag to reorder.",
            add: "Add a value",
            empty: "No company values yet.",
            nameLabel: "Value name",
            namePlaceholder: "Value name",
            descriptionLabel: "Description",
            descriptionPlaceholder: "Describe this value…",
            drag: "Reorder value",
            remove: "Remove value",
            nameRequired: "Value name can't be empty.",
            reordered: "{{name}}, position {{position}} of {{total}}"
        }
    },
    PeoplePage: {
        description: "People and members can be managed in Workleap Classic."
    },
    ManagePeopleList: {
        totalCount_one: "{{count}} person",
        totalCount_other: "{{count}} people",
        noResults: "No people match your search",
        bulkDeactivatedToast: "People deactivated",
        bulkDeactivatedSelfSkippedToast: "People deactivated. You can't deactivate yourself, so your account was skipped.",
        bulkDeactivateErrorToast: "Couldn't deactivate these people. Please try again.",
        bulkReactivatedToast: "People reactivated",
        bulkReactivateErrorToast: "Couldn't reactivate these people. Please try again.",
        bulkInvitedToast_one: "{{count}} person invited.",
        bulkInvitedToast_other: "{{count}} people invited.",
        bulkInvitedPartialToast_one: "{{invited}} of {{total}} people invited.",
        bulkInvitedPartialToast_other: "{{invited}} of {{total}} people invited.",
        bulkSendInviteErrorToast: "Something went wrong. Try again.",
        bulkNobodyInvitedToast: "Nobody could be invited right now.",
        bulkInviteResentToast_one: "{{count}} invite resent.",
        bulkInviteResentToast_other: "{{count}} invites resent.",
        bulkInviteResentPartialToast_one: "{{resent}} of {{total}} invites resent.",
        bulkInviteResentPartialToast_other: "{{resent}} of {{total}} invites resent.",
        bulkResendInviteErrorToast: "Something went wrong. Try again.",
        bulkNobodyResentToast: "No invites were sent. They may have been invited in the last 24 hours.",
        bulkRemovedToast_one: "{{count}} person removed.",
        bulkRemovedToast_other: "{{count}} people removed.",
        bulkRemovedSelfSkippedToast_one: "{{count}} person removed. You can't remove yourself, so your account was skipped.",
        bulkRemovedSelfSkippedToast_other: "{{count}} people removed. You can't remove yourself, so your account was skipped.",
        bulkRemoveErrorToast: "Something went wrong. Try again.",
        selectAllMatching: "Select all {{count}} people",
        empty: {
            title: "No people yet",
            description: "Invite people to your organization to see them here."
        },
        error: {
            title: "Couldn't load people.",
            description: "Please try again later.",
            retry: "Try again",
            refreshBanner: "Couldn't refresh — showing the last results."
        },
        liveRegion: {
            loading: "Loading people",
            loadingMoreAnnouncement: "Loading more people",
            loadedMore_one: "Loaded {{count}} more person",
            loadedMore_other: "Loaded {{count}} more people",
            results_one: "{{count}} person",
            results_other: "{{count}} people",
            sortedAscending: "Table sorted by {{column}}, ascending",
            sortedDescending: "Table sorted by {{column}}, descending"
        }
    },
    ManagePeopleToolbar: {
        searchPlaceholder: "Search by name, email",
        searchAriaLabel: "Search people",
        statusFilter: {
            label: "Status",
            ariaLabel: "Filter by status",
            clearFilter: "Clear filter"
        },
        rolesFilter: {
            label: "Roles",
            ariaLabel: "Filter by roles",
            clearFilter: "Clear filter"
        },
        departmentFilter: {
            label: "Department",
            ariaLabel: "Filter by department",
            clearFilter: "Clear filter"
        },
        managerFilter: {
            label: "Manager",
            ariaLabel: "Filter by manager",
            searchPlaceholder: "Search by name",
            loading: "Loading...",
            noResults: "No matches",
            loadMore: "Load more",
            clearFilter: "Clear filter",
            liveRegion: {
                loadedMore_one: "{{count}} more person loaded",
                loadedMore_other: "{{count}} more people loaded"
            }
        },
        jobFilter: {
            label: "Job",
            ariaLabel: "Filter by job",
            searchPlaceholder: "Search by title",
            loading: "Loading...",
            noResults: "No matches",
            loadMore: "Load more",
            clearFilter: "Clear filter",
            liveRegion: {
                loadedMore_one: "{{count}} more job loaded",
                loadedMore_other: "{{count}} more jobs loaded"
            }
        },
        addFilter: "Add filter",
        removeFilter: "Remove {{filter}} filter",
        resetFilters: "Reset filters"
    },
    ManagePeopleTable: {
        ariaLabel: "People",
        columns: {
            name: "Name",
            roles: "Roles",
            hireDate: "Hire date",
            status: "Status",
            lastSession: "Last session",
            actions: "Actions"
        },
        lastSession: {
            never: "Never",
            today: "Today",
            days_one: "{{count}} day ago",
            days_other: "{{count}} days ago",
            weeks_one: "{{count}} week ago",
            weeks_other: "{{count}} weeks ago"
        }
    },
    managePeople: {
        clearSelection: "Clear selection",
        statuses: {
            active: "Active",
            created: "Created",
            invited: "Invite sent",
            deactivated: "Deactivated",
            nonSeat: "Non-seat"
        },
        roles: {
            admin: "Admin",
            executive: "Executive",
            collaborator: "Collaborator",
            manager: "Manager",
            employee: "Employee"
        },
        permissions: {
            learnMore: "Learn more about permissions",
            learnMoreUrl: "https://help.workleap.com/en/articles/11010624-manage-user-roles-and-permissions"
        },
        unsavedChanges: {
            title: "Leave without saving?",
            body: "Your changes will be lost if you leave this page.",
            stayButton: "Keep editing",
            leaveButton: "Discard changes"
        },
        toasts: {
            deactivatedToast: "Person deactivated",
            deactivateErrorToast: "Couldn't deactivate this person. Please try again.",
            reactivatedToast: "Person reactivated",
            reactivateErrorToast: "Couldn't reactivate this person. Please try again.",
            inviteSentToast: "Invite sent",
            sendInviteErrorToast: "Something went wrong. Try again.",
            inviteResentToast: "Invite resent",
            inviteRecentlySentToast: "Invite already sent recently",
            noInvitableProductToast: "This person isn't assigned to an invitable product",
            resendInviteErrorToast: "Something went wrong. Try again.",
            removedToast: "Person deleted",
            removeErrorToast: "Something went wrong. Try again."
        }
    },
    PermissionOptionList: {
        helperLine: "Everyone gets standard employee access. Add permissions to grant more.",
        roles: {
            admin: { title: "Admin", description: "Manages organization settings, billing, integrations, and permissions." },
            executive: { title: "Executive", description: "Manages the people directory and team structure. Sees analytics and data for every employee." },
            collaborator: { title: "Collaborator", description: "Views settings and reporting. Can't update members." },
            manager: { title: "Manager", description: "Sees data for the people in their reporting line. Granted automatically once someone reports to this person." }
        },
        automaticBadge: "Automatic",
        selfReadOnlyHint: "You can't change your own permissions.",
        adminOnlyHint: "Only Admins can change this.",
        mixedHint: "Some of the selected people have this role. Left as is, each of them keeps their current access."
    },
    ManagePermissionsDialog: {
        title: "Manage permissions for {{name}}",
        bulkTitle_one: "Manage permissions for {{count}} person",
        bulkTitle_other: "Manage permissions for {{count}} people",
        bulkBanner_one: "These permissions apply to the {{count}} selected person. Anything left as is keeps their current access.",
        bulkBanner_other: "These permissions apply to all {{count}} selected people. Anything left as is keeps each person's current access.",
        cancel: "Cancel",
        submit: "Update permissions",
        submitBulk_one: "Update {{count}} person",
        submitBulk_other: "Update {{count}} people",
        successToast: "Permissions updated",
        successBulkToast_one: "Permissions updated for {{count}} person",
        successBulkToast_other: "Permissions updated for {{count}} people",
        updateErrorToast: "We couldn't update permissions. Please try again.",
        selfAdminLockedToast: "You can't change your own permissions.",
        liveRegion: {
            loading: "Loading the selection's current permissions"
        }
    },
    AddPeopleMenu: {
        label: "Add people",
        inviteByEmail: "Invite by email",
        inviteByText: "Invite by text",
        addWithoutSeat: "Add without a seat",
        importFromFile: "Import from a file"
    },
    ManagePeopleHeaderActions: {
        manageSync: "Manage sync",
        syncedFromAt: "Synced from {{integration}} {{timeAgo}}.",
        syncedFrom: "Synced from {{integration}}.",
        syncedAt: "Synced {{timeAgo}}."
    },
    AddWithoutSeatDialog: {
        title: "Add without a seat",
        intro: "These people appear in the directory and org chart. They can't sign in, and they don't use a seat.",
        names: {
            label: "Full names (separate multiple with commas)",
            placeholder: "Alex Kim, Riley Chen",
            tooMany: "Add up to {{count}} people at a time."
        },
        manager: {
            label: "Manager",
            placeholder: "Select a manager",
            description: "Sets who these people report to. You can change it later on their profile.",
            noResults: "No matches",
            searchError: "Couldn't load managers. Please try again.",
            loading: "Searching managers",
            loadingMore: "Loading more managers",
            liveRegion: {
                loadedMore_one: "{{count}} more manager loaded",
                loadedMore_other: "{{count}} more managers loaded"
            }
        },
        cancel: "Cancel",
        submit: "Add people",
        successToast_one: "Person added",
        successToast_other: "{{count}} people added",
        addErrorToast: "Couldn't add these people. Please try again."
    },
    InviteByEmailDialog: {
        title: "Invite by email",
        stepIndicator: "Step {{current}} of {{total}}",
        emails: {
            label: "Email addresses (separate multiple with commas)",
            placeholder: "alex@company.com, riley@company.com",
            invalid_one: "{{emails}} isn't a valid email address.",
            invalid_other: "These aren't valid email addresses: {{emails}}",
            duplicate_one: "{{emails}} is entered more than once.",
            duplicate_other: "These are entered more than once: {{emails}}",
            tooMany: "Invite up to {{count}} people at a time.",
            alreadyMember_one: "{{count}} email already belongs to someone in your organization: {{emails}}",
            alreadyMember_other: "{{count}} emails already belong to someone in your organization: {{emails}}",
            unknown_one: "Couldn't add {{count}} email: {{emails}}",
            unknown_other: "Couldn't add {{count}} emails: {{emails}}",
            checkFailed: "Couldn't verify these emails. Try again."
        },
        manager: {
            label: "Manager",
            placeholder: "Select a manager",
            description: "Sets who these people report to. You can change it later on their profile.",
            noResults: "No matches",
            searchError: "Couldn't load managers. Please try again.",
            loading: "Searching managers",
            loadingMore: "Loading more managers",
            liveRegion: {
                loadedMore_one: "{{count}} more manager loaded",
                loadedMore_other: "{{count}} more managers loaded"
            }
        },
        permissionsLabel: "Permissions",
        cancel: "Cancel",
        continue: "Continue",
        back: "Back",
        sendInvite_one: "Send invite",
        sendInvite_other: "Send {{count}} invites",
        moreOptions: "More invite options",
        createWithoutInviting: "Create without inviting",
        successToast: "Invite sent",
        successBulkToast_other: "Invites sent to {{count}} people",
        sendErrorToast: "Couldn't send the invite. Please try again.",
        createSuccessToast: "Person created",
        createSuccessBulkToast_other: "{{count}} people created",
        createErrorToast: "Couldn't create these people. Please try again."
    },
    InviteByTextDialog: {
        title: "Invite by text",
        stepIndicator: "Step {{current}} of {{total}}",
        phoneNumbers: {
            label: "Phone numbers (separate multiple with commas)",
            placeholder: "+1 555 010 1234, +1 555 010 5678",
            invalid_one: "{{phoneNumbers}} isn't a valid phone number. Include the country code.",
            invalid_other: "These aren't valid phone numbers: {{phoneNumbers}}. Include the country code.",
            duplicate_one: "{{phoneNumbers}} is entered more than once.",
            duplicate_other: "These are entered more than once: {{phoneNumbers}}",
            tooMany: "Invite up to {{count}} people at a time.",
            alreadyMember_one: "{{count}} phone number already belongs to someone in your organization: {{phoneNumbers}}",
            alreadyMember_other: "{{count}} phone numbers already belong to someone in your organization: {{phoneNumbers}}",
            unknown_one: "Couldn't add {{count}} phone number: {{phoneNumbers}}",
            unknown_other: "Couldn't add {{count}} phone numbers: {{phoneNumbers}}",
            checkFailed: "Couldn't verify these phone numbers. Try again."
        },
        manager: {
            label: "Manager",
            placeholder: "Select a manager",
            description: "Sets who these people report to. You can change it later on their profile.",
            noResults: "No matches",
            searchError: "Couldn't load managers. Please try again.",
            loading: "Searching managers",
            loadingMore: "Loading more managers",
            liveRegion: {
                loadedMore_one: "{{count}} more manager loaded",
                loadedMore_other: "{{count}} more managers loaded"
            }
        },
        permissionsLabel: "Permissions",
        cancel: "Cancel",
        continue: "Continue",
        back: "Back",
        sendInvite_one: "Send invite",
        sendInvite_other: "Send {{count}} invites",
        moreOptions: "More invite options",
        createWithoutInviting: "Create without inviting",
        successToast: "Invite sent",
        successBulkToast_other: "Invites sent to {{count}} people",
        sendErrorToast: "Couldn't send the invite. Please try again.",
        createSuccessToast: "Person created",
        createSuccessBulkToast_other: "{{count}} people created",
        createErrorToast: "Couldn't create these people. Please try again."
    },
    ManagePeopleRowMenu: {
        ariaLabel: "Actions for {{name}}",
        edit: "Edit user",
        managePermissions: "Manage permissions",
        sendInvite: "Send invite",
        resendInvite: "Resend invite",
        reactivate: "Reactivate",
        deactivate: "Deactivate",
        remove: "Delete user"
    },
    RemovePersonDialog: {
        title: "Delete this person?",
        body: "This action is permanent and should only be applied to a person who has left your organization. Once deleted, their account can't be restored, and all associated data will be removed from every report and view in Workleap. Changes take effect within a few minutes.",
        confirmationLabel: "I understand",
        confirm: "Delete",
        cancel: "Cancel"
    },
    DeactivatePersonDialog: {
        title: "Deactivate this person?",
        body: "This action is reversible and can be applied to a person on extended leave. You can restore a deactivated account to access all the information and data it contained.",
        confirm: "Deactivate",
        cancel: "Cancel"
    },
    ManagePeopleSelectionBar: {
        selectedCount_one: "{{count}} selected",
        selectedCount_other: "{{count}} selected",
        clear: "Clear",
        managePermissions: "Manage permissions",
        managePermissionsWithCount: "Manage permissions ({{count}})",
        more: "More",
        reactivate: "Reactivate",
        reactivateWithCount: "Reactivate ({{count}})",
        sendInvite: "Send invite",
        sendInviteWithCount: "Send invite ({{count}})",
        resendInvite: "Resend invite",
        resendInviteWithCount: "Resend invite ({{count}})",
        deactivate: "Deactivate",
        deactivateWithCount: "Deactivate ({{count}})",
        remove: "Remove",
        removeWithCount: "Remove ({{count}})"
    },
    DeactivatePeopleDialog: {
        title_one: "Deactivate this person?",
        title_other: "Deactivate {{count}} people?",
        body: "This action is reversible and can be applied to people on extended leave. You can restore a deactivated account to access all the information and data it contained.",
        confirm: "Deactivate",
        cancel: "Cancel"
    },
    RemovePeopleDialog: {
        title_one: "Remove this person?",
        title_other: "Remove {{count}} people?",
        body: "This action is permanent and should only be applied to people who have left your organization. Once removed, their account can't be restored, and all associated data will be removed from every report and view in Workleap. Changes take effect within a few minutes.",
        confirmationLabel: "I understand",
        confirm: "Remove",
        cancel: "Cancel"
    },
    PersonDetailTabsHeading: {
        liveRegion: {
            loading: "Loading person"
        },
        breadcrumb: {
            ariaLabel: "Breadcrumb",
            managePeople: "Manage people"
        }
    },
    PersonDetailPermissionsPage: {
        heading: "Permissions",
        subtitle: "Controls what this user can see and do across Workleap. A person can hold more than one.",
        savedConfirmation: "Changes saved",
        saveErrorToast: "Couldn't save. Please try again.",
        selfAdminLockedError: "You can't change your own permissions.",
        discard: "Discard",
        saveChanges: "Save changes",
        liveRegion: {
            loading: "Loading permissions"
        }
    },
    PersonDetailJobPage: {
        heading: "Job information",
        department: {
            label: "Department",
            placeholder: "Select a department"
        },
        division: {
            label: "Division",
            placeholder: "Select a division"
        },
        manager: {
            label: "Manager",
            placeholder: "Search people",
            noResults: "No people match your search",
            selfError: "A person can't be their own manager.",
            searchError: "Couldn't load people. Please try again.",
            loading: "Searching people",
            loadingMore: "Loading more people",
            liveRegion: {
                loadedMore_one: "{{count}} more person loaded",
                loadedMore_other: "{{count}} more people loaded"
            }
        },
        hireDate: {
            label: "Hire date"
        },
        termination: {
            label: "Termination",
            dateLabel: "Termination date",
            reasonLabel: "Termination reason",
            reasonPlaceholder: "Reason (voluntary, performance, restructure, etc.)",
            beforeHireError: "Termination date can't be before the hire date."
        },
        lockedIconLabel: "Why is this locked?",
        lockedTooltip: "This value is synced with {{integrationName}} and cannot be edited manually.",
        lockedTooltipGeneric: "This value is synced automatically and cannot be edited manually.",
        savedConfirmation: "Changes saved",
        saveErrorToast: "Couldn't save. Please try again.",
        discard: "Discard",
        saveChanges: "Save changes",
        liveRegion: {
            loading: "Loading job information"
        }
    },
    PersonDetailJobHistoryCard: {
        heading: "History",
        addButton: "Add job entry",
        ariaLabel: "Employment history",
        columns: {
            effectiveDate: "Effective date",
            jobTitle: "Job title",
            employmentType: "Employment type",
            hoursPerWeek: "Hours per week",
            actions: "Actions"
        },
        currentBadge: "Current",
        editRow: "Edit {{jobTitle}}",
        deleteRow: "Delete {{jobTitle}}",
        untitledJob: "Untitled job",
        lockedTooltip: "These records are synced with {{integrationName}} and cannot be edited manually.",
        lockedTooltipGeneric: "These records are synced automatically and cannot be edited manually.",
        empty: {
            title: "No job history yet",
            description: "Track {{firstName}}'s roles, employment type, hours, and effective dates over time.",
            descriptionFallback: "Track this person's roles, employment type, hours, and effective dates over time.",
            addButton: "Add first job"
        },
        loadError: {
            title: "Couldn't load the job history.",
            retry: "Try again"
        },
        liveRegion: {
            loading: "Loading job history"
        },
        addedToast: "Job added",
        updatedToast: "Job updated",
        deletedToast: "Job deleted",
        delete: {
            title: "Delete this job?",
            description: "{{jobTitle}} • {{effectiveDate}} — this removes it from {{firstName}}'s history. This action cannot be undone.",
            descriptionFallback: "{{jobTitle}} • {{effectiveDate}} — this removes it from this person's history. This action cannot be undone.",
            confirm: "Delete",
            error: "Couldn't delete. Please try again."
        },
        jobTitle: {
            label: "Job title",
            placeholder: "e.g. Marketing Director",
            searching: "Searching job titles…",
            loadingMore: "Loading more job titles",
            createOption: 'Create "{{title}}"',
            pendingEditHint: 'Pick a suggestion or click "Create" to use this title.',
            liveRegion: {
                loadedMore_one: "{{count}} more job title loaded",
                loadedMore_other: "{{count}} more job titles loaded"
            }
        },
        effectiveDate: {
            label: "Effective date",
            backdateNote: "This will be added to history. It won't change the current job."
        },
        employmentType: {
            label: "Employment type",
            placeholder: "Select...",
            options: {
                FullTime: "Full-time",
                PartTime: "Part-time",
                Intern: "Intern",
                Contractor: "Contractor",
                Freelance: "Freelance",
                Other: "Other"
            }
        },
        hoursPerWeek: {
            label: "Hours per week"
        },
        flsaStatus: {
            label: "FLSA status",
            placeholder: "Select...",
            options: {
                Exempt: "Exempt",
                SalariedNonexempt: "Salaried non-exempt",
                Nonexempt: "Non-exempt",
                Owner: "Owner",
                Other: "Other"
            }
        },
        add: {
            title: "Add new job",
            subtitle: "This becomes {{firstName}}'s active job on the effective date. The previous job will move into history.",
            subtitleFallback: "This becomes the person's active job on the effective date. The previous job will move into history.",
            submit: "Add job"
        },
        edit: {
            title: "Edit job",
            subtitle: "Editing {{effectiveDate}}.",
            subtitleUndated: "Editing this job entry.",
            submit: "Save changes"
        },
        cancel: "Cancel",
        error: {
            required: "This field is required.",
            hoursPositive: "Enter a number greater than 0.",
            save: "Couldn't save. Please try again."
        }
    },
    PersonDetailsPage: {
        liveRegion: {
            loading: "Loading details"
        }
    },
    PersonDetailsForm: {
        savedConfirmation: "Changes saved",
        discard: "Discard",
        saveChanges: "Save changes",
        error: {
            required: "This field is required.",
            futureBirthday: "Birthday can't be in the future.",
            emailRequired: "Work email is required.",
            invalidEmail: "Enter a valid email address.",
            emailUnavailable: "This email address is not available.",
            emailAlreadyMember: "This email address is already used by someone in your organization.",
            emailMaxOrganizations: "This email address has reached the maximum number of Workleap workspaces.",
            VALIDATION_FAILED: "Some of these details couldn't be saved. Check the highlighted fields and try again.",
            NOT_FOUND: "This person no longer exists.",
            generic: "Couldn't save. Please try again.",
            communicationLanguageSaveFailed: "Couldn't save the communication language. Please try again.",
            customFieldsSaveFailed: "Couldn't save the custom fields. Please try again."
        }
    },
    PersonPhotoField: {
        label: "Profile photo",
        changeButton: "Change photo",
        removeButton: "Remove photo",
        cancel: "Cancel",
        lockedIconLabel: "Why is this locked?",
        lockedTooltip: "This value is synced with {{integrationName}} and cannot be edited manually.",
        lockedTooltipGeneric: "This value is synced automatically and cannot be edited manually.",
        uploadedToast: "Profile photo updated",
        removedToast: "Photo removed.",
        crop: {
            title: "Crop photo",
            instructions: "Drag the photo or use the arrow keys to reposition it, and the zoom slider to resize it.",
            areaLabel: "Photo crop area",
            zoomLabel: "Zoom",
            confirm: "Save photo"
        },
        remove: {
            title: "Remove profile photo?",
            description: "{{name}} will show the default avatar instead. This can't be undone.",
            confirm: "Remove"
        },
        error: {
            INVALID_IMAGE_FORMAT: "Choose a PNG, JPEG, or GIF image.",
            IMAGE_TOO_LARGE: "That image is too large. Choose one under 5 MB.",
            generic: "Couldn't upload the photo. Please try again."
        }
    },
    PersonIdentityCard: {
        heading: "Identity",
        firstName: "First name",
        lastName: "Last name",
        preferredName: "Preferred name",
        preferredNameHint: "Overrides the first name everywhere it appears",
        employeeId: "Employee ID",
        employeeIdPlaceholder: "EMP-00000",
        birthday: "Birthday",
        gender: "Gender",
        pronouns: "Pronouns",
        selectPlaceholder: "Select an option",
        lockedIconLabel: "Why is this locked?",
        lockedTooltip: "This value is synced with {{integrationName}} and cannot be edited manually.",
        lockedTooltipGeneric: "This value is synced automatically and cannot be edited manually."
    },
    PersonContactLocationCard: {
        heading: "Contact & location",
        email: "Work email",
        emailUsedElsewhere: "This email is used in another workspace and cannot be changed.",
        emailChecking: "Checking email availability",
        officePhone: "Office phone",
        officePhonePlaceholder: "+1 555 555 5555",
        workLocation: "Work location",
        timeZone: "Timezone",
        timeZonePlaceholder: "Search for a timezone",
        city: "City",
        region: "Region",
        country: "Country",
        selectPlaceholder: "Select an option",
        lockedIconLabel: "Why is this locked?",
        lockedTooltip: "This value is synced with {{integrationName}} and cannot be edited manually.",
        lockedTooltipGeneric: "This value is synced automatically and cannot be edited manually."
    },
    PersonPreferencesCard: {
        heading: "Preferences",
        language: {
            label: "App language",
            placeholder: "Select a language",
            helperText: "Some Workleap Classic features are available in English only.",
            options: {
                "en-US": "English (US)",
                "fr-CA": "French (Canada)"
            } satisfies LocalizedText
        },
        communicationLanguage: {
            label: "Communication language",
            placeholder: "Select a language",
            helperText: "Language used for Workleap notifications and surveys."
        }
    },
    PersonAboutCard: {
        heading: "About",
        bio: "About me",
        bioPlaceholder: "A short introduction",
        interests: "Interests",
        competencies: "Competencies",
        tagPlaceholder: "Add an entry",
        duplicateTag: "That entry is already added.",
        removeTag: "Remove {{tag}}",
        lockedIconLabel: "Why is this locked?",
        lockedTooltip: "This value is synced with {{integrationName}} and cannot be edited manually.",
        lockedTooltipGeneric: "This value is synced automatically and cannot be edited manually."
    },
    PersonCustomFieldsCard: {
        heading: "Custom fields",
        subtitle: "Fields your organization added on top of the Workleap defaults.",
        error: {
            title: "We couldn't load the custom fields.",
            description: "The rest of this person's details are still editable.",
            retry: "Try again"
        }
    },
    PersonCustomField: {
        selectPlaceholder: "Select an option",
        restrictedBadge: "Admins and Executives only",
        lockedIconLabel: "Why is this locked?",
        lockedTooltip: "This value is synced with {{integrationName}} and cannot be edited manually.",
        lockedTooltipGeneric: "This value is synced automatically and cannot be edited manually.",
        tag: {
            placeholder: "Add an entry",
            duplicate: "That entry is already added.",
            remove: "Remove {{tag}}"
        },
        person: {
            placeholder: "Search people",
            noResults: "No people match your search",
            searchError: "Couldn't load people. Please try again.",
            loading: "Searching people",
            loadingMore: "Loading more people",
            liveRegion: {
                loadedMore_one: "{{count}} more person loaded",
                loadedMore_other: "{{count}} more people loaded"
            }
        }
    },
    PersonDetailHeaderActions: {
        deactivate: "Deactivate user",
        reactivate: "Reactivate user",
        sendInvite: "Send invite",
        resendInvite: "Resend invite",
        remove: "Delete user"
    },
    PersonNotFound: {
        heading: "This person doesn't exist or was removed.",
        backToManagePeople: "Back to Manage people"
    },
    PersonDetailErrorBoundary: {
        title: "We couldn't load this person.",
        description: "Please try again later.",
        retry: "Try again"
    },
    PeopleFieldsPage: {
        description: "Employee fields can be managed in Workleap Classic."
    },
    RolesAndPermissionsPage: {
        description: "Roles and permissions can be managed in Workleap Classic."
    },
    AdminConnectedAppsKnowledgeSourcesPage: {
        description: "Choose what Workleap AI can draw on when it answers questions."
    },
    UploadedFilesCard: {
        heading: "Uploaded files",
        description: "Import documentation for Workleap AI to use when answering questions. You can upload up to {{max}} files at a time.",
        uploadButton: "Upload file",
        ariaLabel: "Uploaded files",
        disclosure: {
            title_one: "{{count}} file uploaded",
            title_other: "{{count}} files uploaded"
        },
        columns: {
            fileName: "File name",
            uploadedBy: "Uploaded by",
            uploadedAt: "Upload date",
            status: "Status",
            actions: "Actions"
        },
        status: {
            available: "Available",
            processing: "Processing",
            indexingFailed: "Indexing failed",
            uploading: "Uploading"
        },
        deleteFile: "Delete {{name}}",
        dismissFailure: "Dismiss the error for {{name}}",
        uploadFailed: "{{name}} failed: {{reason}}",
        uploadFailure: {
            unsupportedType: "this file type is unsupported.",
            tooLarge: "the file is larger than {{maxSize}} MB.",
            unexpected: "an unexpected error occurred."
        },
        maxFilesPerUpload: "Uploading the first {{max}} of {{total}} files. Upload the rest separately.",
        liveRegion: {
            loading: "Loading uploaded files"
        },
        empty: {
            title: "No files have been uploaded.",
            description: "Upload a document and Workleap AI will use its content when answering questions."
        },
        loadError: {
            title: "Couldn't load uploaded files",
            retry: "Try again"
        },
        deletedToast: "File deleted.",
        download: {
            error: "Couldn't download the file. Try again."
        },
        delete: {
            title: "Delete this file?",
            description: "Once deleted, Workleap AI won't be able to use this file to answer questions.",
            confirm: "Delete",
            cancel: "Cancel",
            error: "Couldn't delete the file. Try again."
        },
        leaveWhileUploading: {
            title: "Leave while files are uploading?",
            description: "Uploads that haven't finished will not be saved.",
            confirm: "Leave",
            cancel: "Keep uploading"
        }
    },
    WebsitesCard: {
        heading: "Websites",
        description: "Use public website content to enhance responses and insights.",
        addButton: "Add website",
        ariaLabel: "Approved websites",
        faviconAlt: "{{name}} icon",
        disclosure: {
            title_one: "{{count}} website",
            title_other: "{{count}} websites"
        },
        columns: {
            website: "Website",
            status: "Status",
            actions: "Actions"
        },
        status: {
            available: "Available"
        },
        rowMenu: {
            ariaLabel: "Actions for {{name}}",
            edit: "Edit",
            delete: "Delete"
        },
        liveRegion: {
            loading: "Loading websites"
        },
        empty: {
            title: "No websites added",
            description: "Add a public website and Workleap AI will use its content when answering questions."
        },
        loadError: {
            title: "Couldn't load websites",
            retry: "Try again"
        },
        addedToast: "Website added.",
        updatedToast: "Website updated.",
        deletedToast: "Website deleted.",
        delete: {
            title: "Delete this website?",
            description: "Workleap AI will stop using content from {{name}} when answering questions.",
            confirm: "Delete",
            error: "Couldn't delete the website. Try again."
        },
        form: {
            subtitle: "Workleap AI crawls the pages under this address.",
            publicOnlyCallout: "Only publicly accessible websites are supported. Websites behind a login, VPN, or firewall won't be reachable.",
            cancel: "Cancel",
            add: {
                title: "Add website",
                submit: "Add website"
            },
            edit: {
                title: "Edit website",
                submit: "Save changes"
            },
            url: {
                label: "Main website URL",
                placeholder: "https://example.com",
                invalid: "The URL is invalid, make sure to include http:// or https://.",
                duplicate: "This website has already been added."
            },
            name: {
                label: "Display name",
                placeholder: "Optional"
            },
            error: {
                save: "Couldn't save the website. Try again."
            }
        }
    },
    CustomMcpServersCard: {
        heading: "Custom MCP servers",
        description: "Connect your own remote MCP servers to give Workleap AI access to their tools.",
        addButton: "Add server",
        ariaLabel: "Custom MCP servers",
        disclosure: {
            title_one: "{{count}} server",
            title_other: "{{count}} servers"
        },
        columns: {
            server: "Server",
            description: "Description",
            actions: "Actions"
        },
        rowMenu: {
            ariaLabel: "Actions for {{name}}",
            edit: "Edit",
            delete: "Delete"
        },
        liveRegion: {
            loading: "Loading custom MCP servers"
        },
        empty: {
            title: "No custom MCP servers added",
            description: "Add a remote MCP server and Workleap AI will use its tools when answering questions."
        },
        loadError: {
            title: "Couldn't load custom MCP servers",
            retry: "Try again"
        },
        addedToast: "Server added.",
        updatedToast: "Server updated.",
        deletedToast: "Server deleted.",
        delete: {
            title: "Delete {{name}}?",
            description: "Workleap AI won't be able to use this server's tools, and every member's stored access tokens and app registrations for this server will be permanently deleted. This can't be undone.",
            confirm: "Delete",
            error: "Couldn't delete the server. Try again."
        },
        form: {
            cancel: "Cancel",
            oauthCallout: "Only servers that support OAuth are supported. The server must expose a standard OAuth discovery document, and it must be reachable from the public internet.",
            add: {
                title: "Add server",
                subtitle: "Connect a remote MCP server to make its tools available to Workleap AI.",
                submit: "Add server"
            },
            edit: {
                title: "Edit server",
                subtitle: "Only the display name and description can be changed after registration.",
                submit: "Save changes"
            },
            url: {
                label: "Server URL",
                placeholder: "https://example.com/mcp",
                required: "Server URL is required.",
                invalid: "The URL is invalid. Make sure to include https://.",
                httpsRequired: "The server URL must use HTTPS.",
                rejected: "The server was rejected. Make sure it supports OAuth and is reachable from the public internet."
            },
            displayName: {
                label: "Display name",
                required: "Display name is required."
            },
            description: {
                label: "Description",
                required: "Description is required."
            },
            clientId: {
                label: "Client ID (optional)"
            },
            clientSecret: {
                label: "Client secret (optional)",
                requiresClientId: "A client secret requires a client ID."
            },
            error: {
                save: "Couldn't save the server. Try again.",
                notFound: "This server no longer exists. Close this dialog and refresh the list."
            }
        }
    },
    AdminConnectedAppsAccessPage: {
        description: "Slack, Microsoft Teams and ChatGPT access will be managed here."
    },
    IntegrationsPage: {
        description: "Integrations can be managed in Workleap Classic."
    },
    BillingPage: {
        description: "Billing and plan details can be managed in Workleap Classic."
    },
    SegmentsHeading: {
        description: "Reusable filters that narrow reports and the directory to a specific group of people. Rule-based segments stay up to date on their own.",
        newSegment: "New segment"
    },
    SegmentsList: {
        selectAllMatching: "Select all {{count}} segments",
        deletedToast_one: "Segment deleted.",
        deletedToast_other: "{{count}} segments deleted.",
        deleteErrorToast: "Couldn't delete segments. Please try again.",
        renamedToast: "Segment renamed",
        renameErrorToast: "Couldn't rename this segment. Try again.",
        rowDeletedToast: "Segment deleted",
        rowDeleteErrorToast: "Couldn't delete this segment. Try again.",
        empty: {
            noResults: "No segments match '{{term}}'.",
            mine: {
                title: "No segments yet",
                description: "Use New segment to define a reusable group of people."
            },
            shared: {
                title: "Nothing shared with you yet",
                description: "Segments others share with you will appear here."
            }
        },
        error: {
            title: "Couldn't load segments.",
            description: "Please try again later.",
            retry: "Try again",
            refreshBanner: "Couldn't refresh — showing the last results."
        },
        liveRegion: {
            loading: "Loading segments",
            loadingMoreAnnouncement: "Loading more segments",
            loadedMore_one: "Loaded {{count}} more segment",
            loadedMore_other: "Loaded {{count}} more segments",
            results_one: "{{count}} segment",
            results_other: "{{count}} segments"
        }
    },
    SegmentsListToolbar: {
        searchPlaceholder: "Search segments...",
        searchAriaLabel: "Search segments",
        tagsFilter: {
            label: "All tags",
            labelSelected_one: "{{count}} tag",
            labelSelected_other: "{{count}} tags",
            ariaLabel: "Filter by tag",
            clearFilter: "Clear tags"
        }
    },
    SegmentsSelectionBar: {
        selectedCount_one: "{{count}} selected",
        selectedCount_other: "{{count}} selected",
        delete: "Delete",
        clear: "Clear"
    },
    DeleteSegmentsDialog: {
        title_one: "Delete this segment?",
        title_other: "Delete {{count}} segments?",
        body: "Deleting removes these segments. The people in them and their data are not affected. This can't be undone.",
        confirm: "Delete",
        cancel: "Cancel"
    },
    DeleteSegmentDialog: {
        title: "Delete '{{name}}'?",
        body: "People it's shared with will lose access. This can't be undone.",
        confirm: "Delete",
        cancel: "Cancel"
    },
    DeleteJobDialog: {
        title: "Delete '{{title}}'?",
        body: "This can't be undone.",
        confirm: "Delete",
        cancel: "Cancel"
    },
    SegmentRowMenu: {
        ariaLabel: "Actions for {{name}}",
        rename: "Rename",
        delete: "Delete"
    },
    RenameSegmentDialog: {
        title: "Rename segment",
        name: {
            label: "Segment name",
            placeholder: "Name this segment",
            required: "Enter a name for this segment.",
            tooLong: "Keep the name under {{count}} characters."
        },
        save: "Save",
        cancel: "Cancel"
    },
    SegmentsTableView: {
        ariaLabel: "Segments",
        columns: {
            name: "Name",
            tags: "Tags",
            owner: "Owner",
            actions: "Actions"
        }
    },
    SkillLibraryPage: {
        description: "Shape how the agent runs each skill for your organization.",
        liveRegion: {
            loading: "Loading skills"
        },
        features: {
            meetings: { label: "Meetings" },
            engagement: { label: "Engagement" },
            recognition: { label: "Good Vibes" },
            performance_reviews: { label: "Performance reviews" },
            people: { label: "Employee journey" },
            plans: { label: "Plans" }
        }
    },
    agentSkills: {
        skills: {
            wov_engagement_report: {
                name: "Pulse report",
                description: "Sends a weekly summary of how your team's engagement is trending."
            },
            wov_help_me_reply_to_feedback: {
                name: "Help me reply to feedback",
                description: "Drafts a thoughtful response to employee feedback."
            },
            wpm_review: {
                name: "Performance review",
                description: "Gathers the evidence for a review, then drafts, rewrites, and sharpens the answers in your voice."
            },
            wai_people_about: {
                name: "About People",
                description: "Writes a short, sourced profile of a person, scoped to what the viewer can see."
            },
            draft_a_plan: {
                name: "Draft a plan",
                description: "Builds the plan a moment calls for: onboarding, off-boarding, return from leave, improvement, growth, or succession."
            },
            wov_summarize_feedback: {
                name: "Summarize feedback",
                description: "Highlights the themes in your feedback, explains why they matter, and suggests concrete actions."
            },
            wov_feedback_export: {
                name: "Export feedback report",
                description: "Turns your feedback report into a presentation or document ready to share."
            },
            wov_analyze_engagement: {
                name: "Analyze engagement",
                description: "Highlights what stands out in your engagement, eNPS, and participation results, explains why, and suggests next steps."
            },
            wov_export_engagement: {
                name: "Export engagement report",
                description: "Turns your engagement report into a presentation or document ready to share."
            },
            wpm_prepare_meeting_agenda: {
                name: "Prepare a meeting agenda",
                description: "Lays out where the person stands, then what to discuss — each point traced to the record it came from."
            },
            wpm_summarize_meeting: {
                name: "Meeting recap",
                description: "Writes the recap of a 1:1 from what the note-taking apps recorded, without keeping the transcript."
            },
            wpm_organize_meetings: {
                name: "Organize meetings",
                description: "Finds a time everyone can make, books it, and flags the 1:1s that are missing or overdue."
            }
        }
    },
    SkillDetailPage: {
        personalize: {
            title: "Personalize",
            description: "How the agent behaves when it uses this skill.",
            byDefault: "Skill templates",
            byDefaultDescription: "Each one is a different set of instructions the agent can follow. Anything you add becomes available to your whole team.",
            memberAccessLabel: "Member access"
        },
        liveRegion: {
            loading: "Loading skill"
        },
        makeDefault: {
            savedToast: "Default template updated.",
            errorToast: "Couldn't update the default template."
        }
    },
    SkillPersonalizationToggle: {
        label: "Allow people to create their own templates for this skill",
        savedToast: "Personalization setting saved.",
        errorToast: "Couldn't save the personalization setting."
    },
    SkillTemplates: {
        empty: "No templates yet. Create one to add another way of running this skill.",
        newTemplate: "New template",
        newTemplateName: "New template",
        makeDefault: "Make default",
        defaultLabel: "Default",
        toggleCard: "Toggle {{name}}",
        scopeWorkleap: "Workleap",
        scopeOrganization: "Your organization",
        showAll_one: "Show {{count}} more template",
        showAll_other: "Show {{count}} more templates",
        showFewer: "Show fewer",
        standardTemplate: "Standard template",
        standardTemplates: {
            wov_engagement_report: {
                name: "Standard pulse report"
            },
            wov_help_me_reply_to_feedback: {
                name: "Standard feedback reply"
            },
            wpm_review: {
                name: "Balanced and in our voice"
            },
            wai_people_about: {
                name: "Standard introduction"
            }
        }
    },
    SkillResourcePanel: {
        loading: "Loading template",
        error: "Couldn't load this template.",
        retry: "Try again"
    },
    SkillTemplateEditor: {
        nameLabel: "Name",
        namePlaceholder: "Name this template",
        requiredName: "Enter a template name.",
        nameTooLong: "Keep the name under {{count}} characters.",
        contentLabel: "Agent instructions",
        contentDescription: "How the agent should run this skill when it uses this template.",
        contentPlaceholder: "Write the guidance the agent should follow, in plain language.",
        requiredContent: "Enter template instructions.",
        contentTooLong: "Keep the instructions under {{count}} characters.",
        cancelNew: "Cancel",
        discard: "Discard",
        create: "Create template",
        saveChanges: "Save changes",
        delete: "Delete",
        cancel: "Cancel",
        deleteTitle: "Delete this template?",
        deleteBody: "It will stop being available to the agent. This can't be undone.",
        createdToast: "Template created.",
        createErrorToast: "Couldn't create the template.",
        savedToast: "Template saved.",
        saveErrorToast: "Couldn't save the template.",
        deletedToast: "Template deleted.",
        deleteErrorToast: "Couldn't delete the template."
    },
    OrganizationInstructionsEditor: {
        contentLabel: "Organization guidance",
        contentDescription: "Added on top of whichever template runs, including ones you create. Leave it empty to remove it.",
        contentPlaceholder: "Write the guidance the agent should follow, in plain language.",
        contentTooLong: "Keep the instructions to {{count}} characters or fewer.",
        discard: "Discard",
        saveChanges: "Save changes",
        savedToast: "Organization guidance saved.",
        saveErrorToast: "Couldn't save the organization guidance."
    },
    SkillFeatureInstructions: {
        title: "Feature instructions",
        description: "Read only here. These instructions apply to every template for this skill.",
        organizationTitle: "Organization guardrails",
        organizationDescription: "Inherited from this feature's settings.",
        emptyOrganization: "No organization guardrails are configured.",
        organizationUnavailable: "Organization guardrails couldn't be loaded.",
        liveRegion: {
            loading: "Loading feature instructions"
        }
    },
    JobsPage: {
        description: "Jobs can be managed in Workleap Classic."
    },
    JobArchitecture: {
        breadcrumb: {
            ariaLabel: "Breadcrumb",
            jobs: "Jobs",
            jobArchitecture: "Architecture"
        },
        title: "Job Architecture",
        description: "Functions group your jobs by the type of work they do. Levels define seniority. Together they shape compensation bands, performance reviews, and career paths.",
        actions: {
            discard: "Discard",
            save: "Save changes"
        },
        nameRequired: "Name is required.",
        nameDuplicate: "Another new row already uses this name.",
        nameAlreadyInUse: "This name is already in use.",
        saveErrorRow: "Couldn't save this. Try again.",
        saveSuccessToast: "Job architecture updated.",
        saveErrorToast: "Some changes couldn't be saved.",
        editFunctionSuccessToast: "Function updated.",
        editFunctionErrorToast: "Couldn't update the function. Try again.",
        editFunctionNotFoundToast: "This function no longer exists.",
        editLevelSuccessToast: "Level updated.",
        editLevelErrorToast: "Couldn't update the level. Try again.",
        editLevelNotFoundToast: "This level no longer exists."
    },
    JobFunctionsCard: {
        title: "Functions",
        description: "Group jobs by the type of work they do.",
        columns: {
            name: "Name",
            jobs: "Jobs"
        },
        jobsCount_one: "{{count}} job",
        jobsCount_other: "{{count}} jobs",
        noJobs: "No jobs",
        jobsCountAriaLabel_one: "{{count}} job in {{name}}",
        jobsCountAriaLabel_other: "{{count}} jobs in {{name}}",
        addFunction: "Add function",
        deleteFunction: "Delete {{name}}",
        draftNameAriaLabel: "Name for new function {{position}}",
        removeDraft: "Remove new function {{position}}",
        empty: "No functions yet.",
        loadError: {
            title: "Couldn't load the functions.",
            retry: "Try again",
            retrying: "Retrying"
        },
        liveRegion: {
            loading: "Loading functions"
        }
    },
    JobLevelsCard: {
        title: "Levels",
        description: "Define seniority by scope, complexity, and impact.",
        columns: {
            number: "#",
            name: "Name",
            individualContributor: "Individual contributor",
            manager: "Manager"
        },
        nameAriaLabel: "Name for level {{number}}",
        individualContributorAriaLabel: "Individual contributor track for {{name}}",
        managerAriaLabel: "Manager track for {{name}}",
        draftIndividualContributorAriaLabel: "Individual contributor track for new level {{position}}",
        draftManagerAriaLabel: "Manager track for new level {{position}}",
        draftNameAriaLabel: "Name for new level {{position}}",
        addLevel: "Add level",
        deleteLevel: "Delete {{name}}",
        removeDraft: "Remove new level {{position}}",
        empty: "No levels yet.",
        loadError: {
            title: "Couldn't load the levels.",
            retry: "Try again",
            retrying: "Retrying"
        },
        liveRegion: {
            loading: "Loading levels"
        }
    },
    JobsList: {
        manageJobArchitecture: "Manage job architecture",
        addJobs: "Add jobs",
        totalCount_one: "{{count}} job",
        totalCount_other: "{{count}} jobs",
        selectAllMatching_one: "Select the {{count}} matching job",
        selectAllMatching_other: "Select all {{count}} matching jobs",
        noResults: "No jobs match your filters",
        empty: {
            title: "Your architecture is ready. Now add jobs.",
            description: "Jobs live inside the functions and levels you just set up. Once they're added, you can use them in performance reviews and compensation cycles.",
            addJob: "Add job"
        },
        error: {
            title: "Couldn't load jobs.",
            description: "Please try again later.",
            retry: "Try again",
            refreshBanner: "Couldn't refresh - showing the last results."
        },
        liveRegion: {
            loading: "Loading jobs...",
            loadedMore: "Loaded {{count}} more jobs.",
            results: "{{count}} jobs loaded.",
            sortedAscending: "Sorted by {{column}}, ascending.",
            sortedDescending: "Sorted by {{column}}, descending."
        }
    },
    JobsToolbar: {
        searchPlaceholder: "Search by title",
        searchAriaLabel: "Search jobs",
        statusFilter: {
            label: "Status",
            ariaLabel: "Filter by status",
            reset: "Reset to default"
        },
        functionFilter: {
            label: "Function",
            ariaLabel: "Filter by function",
            clearAriaLabel: "Clear function filter",
            any: "All",
            deleted: "Deleted function"
        },
        levelFilter: {
            label: "Level",
            ariaLabel: "Filter by level",
            clearAriaLabel: "Clear level filter",
            any: "All"
        },
        resetFilters: "Reset filters"
    },
    JobsTable: {
        ariaLabel: "Jobs",
        columns: {
            title: "Title",
            function: "Function",
            level: "Level",
            people: "People",
            status: "Status"
        },
        status: {
            draft: "Draft",
            active: "Active",
            archived: "Archived"
        },
        noFunction: "-",
        noLevel: "-"
    },
    JobsSelectionBar: {
        selectedCount_one: "{{count}} selected",
        selectedCount_other: "{{count}} selected",
        clear: "Clear",
        edit: "Edit",
        setFunction: "Set function",
        setLevel: "Set level",
        setTrack: "Set track",
        addCompetency: "Add competency"
    },
    JobsBulkEditDialog: {
        function: {
            title_one: "Set function for {{count}} job",
            title_other: "Set function for {{count}} jobs",
            label: "Function",
            placeholder: "Select a function",
            submit_one: "Set function for {{count}} job",
            submit_other: "Set function for {{count}} jobs"
        },
        level: {
            title_one: "Set level for {{count}} job",
            title_other: "Set level for {{count}} jobs",
            description_one: "Assign a track and level to the selected job.",
            description_other: "Assign a track and level to the selected jobs.",
            label: "Level",
            placeholder: "Select a level",
            trackNotSupported: "This track isn't available at the selected level.",
            submit_one: "Set level for {{count}} job",
            submit_other: "Set level for {{count}} jobs"
        },
        track: {
            title_one: "Set track for {{count}} job",
            title_other: "Set track for {{count}} jobs",
            label: "Track",
            placeholder: "Select a track",
            options: {
                individualContributor: "Individual contributor",
                manager: "Manager"
            },
            submit_one: "Set track for {{count}} job",
            submit_other: "Set track for {{count}} jobs"
        },
        competencies: {
            title_one: "Add a competency to {{count}} job",
            title_other: "Add a competency to {{count}} jobs",
            label: "Competencies to add",
            placeholder: "Add a competency",
            hint: "These are added to each selected job. Competencies already on a job are kept.",
            submit_one: "Add to {{count}} job",
            submit_other: "Add to {{count}} jobs"
        },
        partialFailure: {
            updated_one: "{{count}} job updated.",
            updated_other: "{{count}} jobs updated.",
            skipped: {
                trackNotSupportedByLevel_one: "{{count}} job was skipped because the chosen track isn't available at its level.",
                trackNotSupportedByLevel_other: "{{count}} jobs were skipped because the chosen track isn't available at their level."
            }
        },
        successToast_one: "{{count}} job updated.",
        successToast_other: "{{count}} jobs updated.",
        errorToast: "Couldn't update the selected jobs. Please try again.",
        validationErrorToast: "This change can't be applied to the selected jobs.",
        cancel: "Cancel"
    },
    JobFunctionDeleteDialog: {
        title: "Delete '{{name}}'?",
        confirmBody: "Are you sure you want to delete '{{name}}'? This action can't be undone.",
        reassignBody_one: "{{count}} job is assigned to this function, impacting {{people}}. Reassign it to continue.",
        reassignBody_other: "{{count}} jobs are assigned to this function, impacting {{people}}. Reassign them to continue.",
        reassignBodyWithoutPeople_one: "{{count}} job is assigned to this function. Reassign it to continue.",
        reassignBodyWithoutPeople_other: "{{count}} jobs are assigned to this function. Reassign them to continue.",
        people_one: "{{count}} employee",
        people_other: "{{count}} employees",
        targetLabel: "Move all jobs to",
        targetPlaceholder: "Select a function",
        delete: "Delete",
        reassignAndDelete: "Reassign and delete",
        cancel: "Cancel",
        successToast: "Function deleted.",
        errorToast: "Couldn't delete the function. Try again.",
        validationErrorToast: "This function is still assigned to jobs. Reassign them and try again.",
        liveRegion: {
            loading: "Loading impacted employees"
        }
    },
    JobLevelDeleteDialog: {
        blockedTitle: '"{{name}}" can\'t be deleted',
        blockedBody_one: "{{count}} job is assigned to this level, impacting {{people}}. Reassign it to another level before deleting.",
        blockedBody_other: "{{count}} jobs are assigned to this level, impacting {{people}}. Reassign them to another level before deleting.",
        people_one: "{{count}} employee",
        people_other: "{{count}} employees",
        viewJobs_one: "View {{count}} job",
        viewJobs_other: "View {{count}} jobs",
        confirmTitle: "Delete level?",
        confirmBody: 'Are you sure you want to delete "{{name}}"? This action can\'t be undone.',
        delete: "Delete",
        cancel: "Cancel",
        successToast: "Level deleted.",
        errorToast: "Couldn't delete the level. Try again.",
        loadingLabel: "Checking where this level is used",
        usageError: {
            title: "Couldn't check where this level is used.",
            retry: "Try again",
            retrying: "Trying again"
        }
    },
    JobArchitectureGetStartedPage: {
        eyebrow: "Get started",
        label: "Set up your job architecture",
        description: "Set it up once, and pay ranges, reviews, and career paths all build on it.",
        recommended: "Recommended",
        aiOption: {
            title: "Set up with AI",
            description: "Describe your org or upload a doc, and the agent drafts your architecture.",
            link: "Set up with AI"
        },
        manualOption: {
            title: "Build manually",
            description: "Create your functions, levels, and tracks step by step.",
            link: "Start setup"
        },
        addPeopleLink: "Add your people in Workleap at any time"
    },
    JobArchitectureAiSetupWizard: {
        title: "Set up your job architecture",
        steps: {
            describe: "Upload",
            architecture: "Architecture",
            jobTitles: "Job titles"
        },
        stepperAriaLabel: "Setup progress",
        close: "Close",
        liveRegion: {
            loading: "Drafting your job architecture…"
        },
        suggestedCount_one: "{{count}} job function suggested.",
        suggestedCount_other: "{{count}} job functions suggested.",
        jobMappingsSuggestedCount_one: "{{count}} job title mapping generated.",
        jobMappingsSuggestedCount_other: "{{count}} job title mappings generated.",
        architectureError: {
            timeout: {
                title: "This is taking longer than expected.",
                description: "The request timed out before a job architecture came back."
            },
            noSuggestions: {
                title: "Nothing came back.",
                description: "The agent didn't generate any job functions this time."
            },
            generic: {
                title: "Couldn't determine job architecture.",
                description: "Something went wrong. Please try again."
            },
            retry: "Try again",
            retrying: "Retrying",
            startOver: "Start over"
        },
        reviewingJobTitles: "Reviewing your job titles…",
        jobMappingsError: {
            timeout: {
                title: "This is taking longer than expected.",
                description: "The request timed out before job title mappings came back."
            },
            noSuggestions: {
                title: "Nothing came back.",
                description: "The agent didn't map any job titles this time."
            },
            generic: {
                title: "Couldn't determine job mappings.",
                description: "Something went wrong. Please try again."
            },
            retry: "Try again",
            retrying: "Retrying",
            back: "Back to architecture"
        },
        finishSetupSuccessToast_one: "{{count}} job title saved as a draft.",
        finishSetupSuccessToast_other: "{{count}} job titles saved as drafts.",
        finishSetupErrorToast: "Couldn't finish setup. Please try again."
    },
    JobArchitectureDescribeStep: {
        eyebrow: "Stage 1 of 3",
        title: "Describe your job architecture",
        description: "Tell the agent how your roles are organized and it drafts your functions and levels. Add your documents below.",
        addFiles: "Add files",
        addFilesHelp: "Leveling guides, career frameworks, or a job matrix (PDF, Word, Excel, CSV). An HRIS brings your titles and people in too.",
        maxFilesTooltip: "You can add up to 5 files.",
        selectedFilesLabel: "Selected files",
        suggestPrompt: "Not sure where to start?",
        suggestLink: "Suggest one for me",
        buildButton: "Build my job architecture"
    },
    JobHeader: {
        breadcrumb: {
            ariaLabel: "Breadcrumb",
            jobs: "Jobs",
            editJob: "Edit job"
        },
        title: {
            placeholder: "Job title"
        },
        actions: {
            label: "Job actions",
            generateDescription: "Generate description with AI",
            archive: "Archive",
            delete: "Delete"
        }
    },
    JobPage: {
        title: {
            required: "Title is required",
            duplicate: "A job with this title already exists."
        },
        function: {
            required: "Function is required",
            createErrorToast: "Couldn't create the function. Please try again."
        },
        liveRegion: {
            loading: "Loading job...",
            submitBlocked: "This job can't be saved yet. Check the highlighted fields."
        },
        saveErrorToast: "Failed to save job. Please try again.",
        saveSuccessToast: "Job saved successfully.",
        statusErrorToast: "Job saved, but the status change failed. Please try again.",
        statusOnlyErrorToast: "Failed to change status. Please try again.",
        unsavedChanges: {
            title: "Leave without saving?",
            body: "Your changes will be lost if you leave this page.",
            stayButton: "Keep editing",
            leaveButton: "Discard changes"
        },
        archiveSuccessToast: "Job archived.",
        archiveBlockedToast: "This job can't be archived while people are still assigned to it.",
        archiveErrorToast: "Failed to archive job. Please try again.",
        deleteSuccessToast: "Job deleted.",
        deleteBlockedToast: "This job can't be deleted because it appears in one or more employees' employment history.",
        deleteErrorToast: "Failed to delete job. Please try again.",
        generateDescription: {
            successToast: "Description drafted with AI.",
            errorToast: "Couldn't generate a description. Please try again.",
            timeoutErrorToast: "This is taking longer than expected. Please try again.",
            noDescriptionErrorToast: "The AI couldn't generate a description for this job. Please try again.",
            contextChangedToast: "The job's details changed while generating. Please try again."
        },
        actions: {
            discard: "Discard",
            update: "Update"
        }
    },
    JobFormFields: {
        description: {
            label: "Description",
            placeholder: "Describe what this job is responsible for.",
            aiGeneratingBanner: "Generating a description with AI…",
            aiDraftedBanner: "Drafted by AI — review before saving.",
            undo: "Undo",
            dismiss: "Dismiss"
        },
        competencies: {
            label: "Competencies",
            placeholder: "Add a competency"
        }
    },
    JobFunctionComboBox: {
        label: "Function",
        placeholder: "Select a function",
        noMatches: "No matching functions",
        create: 'Create "{{name}}"'
    },
    JobFormSidebar: {
        track: {
            label: "Track",
            placeholder: "Select a track",
            options: {
                individualContributor: "Individual contributor",
                manager: "Manager"
            }
        },
        level: {
            label: "Level",
            placeholder: "Select a level",
            none: "None",
            trackNotSupported: "This track isn't available at the selected level."
        },
        people: {
            label: "People",
            none: "None",
            count_one: "{{count}} person",
            count_other: "{{count}} people"
        },
        status: {
            label: "Status",
            placeholder: "Select a status",
            options: {
                draft: "Draft",
                active: "Active",
                archived: "Archived"
            }
        },
        lastUpdated: "Updated {{timeAgo}}",
        relativeTime: {
            today: "today",
            days_one: "{{count}} day ago",
            days_other: "{{count}} days ago",
            weeks_one: "{{count}} week ago",
            weeks_other: "{{count}} weeks ago"
        }
    },
    NewJobPage: {
        breadcrumb: {
            ariaLabel: "Breadcrumb",
            jobs: "Jobs",
            newJob: "New job"
        },
        title: {
            placeholder: "Job title",
            required: "Title is required",
            duplicate: "A job with this title already exists."
        },
        function: {
            required: "Function is required",
            createErrorToast: "Couldn't create the function. Please try again."
        },
        liveRegion: {
            loading: "Loading options...",
            submitBlocked: "This job can't be saved yet. Check the highlighted fields."
        },
        saveErrorToast: "Failed to save job. Please try again.",
        saveSuccessToast: "Job saved successfully.",
        actions: {
            discard: "Discard",
            saveAsDraft: "Save as draft",
            activate: "Activate"
        }
    },
    JobNotFound: {
        heading: "Job not found",
        body: "This job doesn't exist or is no longer available.",
        backToJobs: "Back to Jobs"
    },
    JobArchitectureLoadingSteps: {
        title: "Drafting your architecture",
        titleJobMappings: "Reviewing your job titles",
        steps: {
            readingDocument: "Reading your document",
            extractingFunctions: "Extracting job functions",
            extractingLevels: "Extracting job levels",
            preparingForReview: "Preparing functions and levels for your review",
            jobMappings: {
                matchingTitles: "Matching your job titles",
                checkingConfidence: "Checking mapping confidence",
                preparingForReview: "Preparing job title mappings for your review"
            }
        }
    },
    JobArchitectureEditStep: {
        eyebrow: "Stage 2 of 3",
        title: "Architecture",
        description: "Every job maps to a function, a level, and a track. Set up those building blocks here.",
        jobFunctionsTitle: "Functions",
        jobFunctionsDescription: "A function groups related jobs, like Engineering or Go-to-market.",
        jobLevelsTitle: "Levels",
        jobLevelsDescription: "A level can apply to the individual contributor track, the manager track, or both.",
        columns: {
            functionName: "Function name",
            number: "#",
            level: "Level",
            individualContributor: "Individual contributor",
            manager: "Manager",
            individualContributorFor: "Individual contributor track for {{name}}",
            managerFor: "Manager track for {{name}}"
        },
        addFunction: "Add function",
        deleteFunction: "Delete {{name}}",
        addLevel: "Add level",
        deleteLevel: "Delete {{name}}",
        reviewJobTitles: "Review job titles",
        reviewJobTitlesDisabledHint: "Give every function and level a unique name to continue."
    },
    JobArchitectureJobTitlesStep: {
        eyebrow: "Stage 3 of 3",
        title: "Job titles",
        description: "Each title, mapped to a function, level, and track. Reject anything that's wrong; the rest save as drafts you activate when you're ready.",
        approved: "approved",
        toReview: "to review",
        rejected: "rejected",
        mappingsTitle: "High confidence",
        empty: "No job title mappings were generated.",
        needsReviewTitle: "Needs review",
        columns: {
            jobTitle: "Title",
            function: "Function",
            level: "Level",
            track: "Track",
            functionFor: "Function for {{name}}",
            levelFor: "Level for {{name}}",
            trackFor: "Track for {{name}}"
        },
        individualContributor: "IC",
        manager: "Manager",
        approve: "Approve",
        approvedLabel: "Approved",
        approveAriaLabel: "Approve {{name}}",
        approvedAriaLabel: "Approved {{name}}",
        approveAll: "Approve all {{count}}",
        reject: "Reject {{name}}",
        undoReject: "Undo reject of {{name}}",
        finishSetup: "Finish setup",
        finishSetupDisabledHint: "Review every job title, and approve at least one, to finish setup.",
        finishingSetup: "Finishing setup…"
    },
    ManageReviewCyclesPage: {
        description: "Review cycles can be created and managed in Workleap Performance."
    },
    FeaturesPage: {
        loadError: {
            title: "Couldn't load features",
            description: "Please try again later.",
            retry: "Try again",
            retrying: "Retrying"
        },
        liveRegion: {
            loading: "Loading features"
        }
    },
    FeaturesTable: {
        ariaLabel: "Features",
        columns: {
            feature: "Feature",
            access: "Who can use it"
        },
        features: {
            engagement: {
                name: "Engagement",
                description: "Pulse surveys and the feedback channel"
            },
            goals: {
                name: "Goals",
                description: "Individual, team, and company objectives, and progress against them"
            },
            "good-vibes": {
                name: "Good Vibes",
                description: "Recognition people send each other, tied to your core values"
            },
            meetings: {
                name: "Meetings",
                description: "Recurring 1:1s between managers and their reports, with agendas and notes"
            },
            "performance-reviews": {
                name: "Performance reviews",
                description: "Recurring cycles of self, peer, and manager assessments, with calibration"
            },
            plans: {
                name: "Plans",
                description: "Onboarding, offboarding, and growth plans assigned to a person"
            }
        },
        access: {
            everyone: "Everyone",
            noOne: "No one",
            partial_one: "{{included}} of {{count}} person",
            partial_other: "{{included}} of {{count}} people"
        }
    },
    EngagementSettingsPage: {
        description: "Engagement settings are managed in Workleap Classic.",
        openSettings: "Open engagement settings"
    },
    GoodVibesSettingsPage: {
        description: "Good Vibes settings are managed in Workleap Classic.",
        openSettings: "Open Good Vibes settings"
    },
    ReviewCycleDetailHeading: {
        origin: {
            settings: "Manage review cycles",
            home: "Home"
        }
    },
    NewReviewCyclePage: {
        creating: "Creating your review cycle",
        error: "We couldn't create the review cycle. Please try again."
    },
    ReviewCycleBuilderPage: {
        loading: "Loading your review cycle"
    },
    CycleBuilderForm: {
        pageTitle: "Review cycle setup",
        nav: {
            label: "Jump to section",
            sectionHasError: "Needs attention"
        },
        sections: {
            basics: "Basics",
            reviewForm: "Review form",
            schedule: "Schedule",
            reviewees: "Reviewees",
            reviewScore: "Review score",
            nineBox: "9-box",
            acknowledgment: "Formal acknowledgment",
            notifications: "Notifications summary"
        },
        notificationsDescription: "Everything this cycle will send, and who receives it.",
        sectionDescriptions: {
            reviewScore: "One number, rolled up from the answers.",
            nineBox: "Place each participant on performance and potential."
        },
        sectionPlaceholder: "Nothing to configure here yet.",
        name: {
            label: "Cycle name",
            placeholder: "e.g. H1 2026 Performance Review",
            required: "Enter a name for this cycle."
        },
        description: {
            label: "Description",
            placeholder: "Explain the purpose of this cycle to reviewers"
        },
        reviewTypes: {
            label: "Review types"
        },
        acknowledgment: {
            label: "Reviewees must confirm they have read their results",
            eligible: "Once acknowledged, a review can no longer be unshared."
        },
        scheduleErrors: {
            evaluationEndBeforeStart: "The evaluation period must end on or after it starts.",
            closeNotAfterOpen: "The cycle must close after it opens.",
            closeNotAfterDueDate: "The cycle must close after {{date}}, its last submission deadline.",
            closeNotAfterLastOpening: "The cycle must close after the last review opens.",
            closeNotAfterFinalDueDate: "The cycle must close after {{date}}, the last reviewee's final deadline.",
            dueDateBeforeOpen: {
                self: "Self reviews can't be due before the cycle launches.",
                manager: "Manager reviews can't be due before the cycle launches.",
                peer: "Peer reviews can't be due before the cycle launches.",
                upward: "Upward reviews can't be due before the cycle launches."
            }
        }
    },
    CycleScheduleSection: {
        type: {
            label: "Cycle type",
            focal: {
                label: "Focal",
                description: "Everyone is reviewed on the same dates."
            },
            hireDate: {
                label: "Hire date",
                description: "Each person's review opens on their own date."
            },
            basedOn: {
                label: "Based on",
                anniversary: "Hire-date anniversary",
                milestone: "Tenure milestone"
            },
            grouping: {
                label: "Grouping",
                none: "None",
                weekly: "Weekly cohorts",
                monthly: "Monthly cohorts"
            },
            milestone: {
                label: "Milestone",
                "30": "After 30 days",
                "60": "After 60 days",
                "90": "After 90 days",
                "180": "After 6 months",
                "365": "After 1 year"
            }
        },
        evaluationPeriod: {
            label: "Evaluation period",
            hint: "The work being reviewed.",
            startDate: "Start date",
            endDate: "End date",
            lengthLabel: "Evaluation period length",
            lengthPrefix: "Assess the",
            sinceHirePrefix: "Assess",
            lengthSuffix: "leading up to it.",
            months_one: "{{count}} month",
            months_other: "{{count}} months",
            sinceHire: "everything since they were hired",
            placeholder: "Choose a length"
        },
        mode: {
            automatic: "Automatically",
            manual: "Manually"
        },
        open: {
            focalLabel: "Cycle launches",
            hireDateLabel: "Reviews open",
            focalDateLabel: "Launch date",
            hireDateDateLabel: "Opening date",
            stillManual: "You can still launch manually.",
            intake: {
                label: "Reviews keep opening for",
                unitLabel: "Duration unit",
                weeks: "weeks",
                months: "months",
                suffix: "after the first review opens",
                lastOpening: "The last review opens on {{date}}. A cycle can span at most one year.",
                cap: "A cycle can span at most one year."
            }
        },
        close: {
            label: "Cycle closes",
            hint: "All reviews lock and become read-only.",
            dateLabel: "Close date",
            floor: "Must be {{date}} or later, so everyone has time to finish."
        },
        lateSubmissions: {
            label: "Late submissions",
            notAccepted: "Not accepted",
            acceptedFor: "Accepted for…",
            indefinitely: "Accepted indefinitely",
            daysLabel: "Days after the review is due",
            daysSuffix_one: "day after the review is due",
            daysSuffix_other: "days after the review is due",
            help: {
                notAccepted: "Reviews lock after the due date passes.",
                acceptedFor: "After that, the review locks and becomes read-only.",
                indefinitely: "Reviews never lock. You can close and lock the cycle yourself."
            }
        },
        reviewWindows: {
            label: "Review windows",
            focalHint: "Overdue reviews can still be submitted until the cycle closes."
        },
        recurrence: {
            label: "Recurrence",
            switchLabel: "Repeat this cycle",
            nextCycle: "The next cycle starts on {{startDate}}. Its draft is created a month ahead, on {{draftDate}}.",
            nextCycleFromLaunch: "The next cycle starts one evaluation period after this one launches. Its draft is created a month ahead.",
            nextCycleFromLastOpening: "The next cycle starts the day after the last review opens. Its draft is created a month ahead."
        }
    },
    CycleReviewWindows: {
        reviewTypes: {
            self: "Self review",
            manager: "Manager review",
            peer: "Peer review",
            upward: "Upward review"
        },
        table: {
            caption: "Due dates by review type",
            reviewType: "Review type",
            deadline: "Submission deadline",
            dueAfter: "Due after reviews open",
            days: "days",
            deadlineLabel: "{{reviewType}} submission deadline",
            dueAfterLabel: "{{reviewType}} due after reviews open, in days"
        },
        timeline: {
            sliderLabel: "{{reviewType}} due date",
            dueOn: "Due {{date}}",
            dueOnDay: "Due day {{day}}",
            noDueDate: "No due date",
            launch: "Launch",
            day: "Day {{day}}",
            overflow: {
                self: "Self reviews lock after the cycle closes.",
                manager: "Manager reviews lock after the cycle closes.",
                peer: "Peer reviews lock after the cycle closes.",
                upward: "Upward reviews lock after the cycle closes."
            }
        }
    },
    CycleNotificationsSummary: {
        subheading: "This is an overview of all automated notifications that will be sent during the performance review cycle. These notifications help keep participants informed and ensure timely completion of reviews.",
        filterLabel: "Filter by recipient",
        tableLabel: "Automated notifications",
        columns: {
            notification: "Notification",
            goesTo: "Goes to",
            sent: "Sent"
        },
        audiences: {
            all: "All",
            reviewer: "Reviewer",
            directManager: "Direct manager",
            reviewee: "Reviewee",
            peerReviewer: "Peer reviewer",
            upwardReviewer: "Upward reviewer"
        },
        notifications: {
            reviewCycleKickoff: {
                title: "Review cycle kickoff",
                description: "Notifies participants that the review cycle has launched and provides an overview of the process.",
                trigger: "Sent when the cycle starts"
            },
            peerReviewersSelection: {
                title: "Peer reviewers selection",
                description: "Notifies managers to select peer reviewers for their direct reports.",
                trigger: "Sent when the cycle starts"
            },
            peerReviewRequest: {
                title: "Peer review request",
                description: "Notifies participants to complete their peer reviews.",
                trigger: "Sent when peer reviewers are selected"
            },
            upwardReviewersSelection: {
                title: "Upward reviewers selection",
                description: "Notifies managers to select upward reviewers for their direct reports.",
                trigger: "Sent when the cycle starts"
            },
            upwardReviewRequest: {
                title: "Upward review request",
                description: "Notifies participants to complete their upward reviews.",
                trigger: "Sent when upward reviewers are selected"
            },
            temporaryManagerAssignation: {
                title: "Temporary manager assignation",
                description: "Notifies participants that they have been assigned as temporary managers for direct reports of other managers.",
                trigger: "Sent when they are assigned as temporary managers"
            },
            reviewReminder: {
                title: "Review reminder",
                description: "Reminds participants to complete their reviews before the due date.",
                trigger: "Sent 3 business days before submission deadline"
            },
            reviewDueToday: {
                title: "Review due today",
                description: "Reminds participants that their review is due today.",
                trigger: "Sent on submission deadline"
            },
            reviewOverdue: {
                title: "Review overdue",
                description: "Notifies participants that their review is overdue.",
                trigger: "Sent after the submission deadline has passed"
            },
            submitConfirmation: {
                title: "Review submission confirmation",
                description: "Confirms to participants that their reviews have been successfully submitted.",
                trigger: "Sent when the review is submitted"
            },
            managerDailyDigest: {
                title: "Manager daily digest",
                description: "Provides managers with a daily summary of activity for their direct reports: completed reviews, overdue reviews, and direct reports recently added or removed.",
                trigger: "Sent daily when there is activity to report"
            },
            reviewShared: {
                title: "Review shared",
                description: "Notifies reviewees that the manager, peer and/or upward reviews have been shared with them.",
                trigger: "Sent when reviews are shared"
            },
            newlyAddedParticipant: {
                title: "Newly added participants",
                description: "Informs new participants that they were added to an ongoing review cycle after its launch and that tasks have been assigned to them.",
                trigger: "Sent when they are added to an ongoing cycle"
            }
        }
    },
    ReviewScoreField: {
        scaleLabel: "Review score scale",
        scaleOption: "Out of {{scale}}",
        modeLabel: "Formula",
        mode: {
            "average-manager-questions": {
                label: "Average the manager's rating answers",
                description: "Every rating answer the manager gave, averaged."
            },
            "average-all-questions": {
                label: "Average every reviewer's rating answers",
                description: "Every rating answer from every reviewer, averaged."
            },
            custom: {
                label: "Custom formula",
                description: "Choose which ratings count, and by how much."
            }
        }
    },
    NineBoxField: {
        inclusionLabel: "Include a 9-box grid",
        performanceLabel: "Performance",
        performanceMode: {
            "overall-score": {
                label: "Review score",
                description: "Use the score this cycle already calculates."
            },
            custom: {
                label: "Custom",
                description: "Pick specific rating questions and assign each a weight."
            }
        },
        potentialLabel: "Potential",
        potentialMode: {
            "single-question": {
                label: "Single question",
                description: "Score this axis from one rating question."
            },
            custom: {
                label: "Custom",
                description: "Pick specific rating questions and assign each a weight."
            }
        },
        questionLabel: "Question",
        questionPlaceholder: "Select a question",
        reviewTypeLabel: "Review type",
        reviewTypePlaceholder: "Select",
        scaleLabel: "Reporting scale",
        reviewType: {
            manager: "Manager",
            self: "Self",
            peer: "Peer",
            upward: "Upward"
        }
    },
    NoRatingQuestionsHint: {
        message: "No rating questions, so this cycle will not produce a score.",
        addRatingQuestion: "Add a rating question"
    },
    ScoreFormulaQuestionSelect: {
        untitledQuestion: "Untitled question",
        addRatingQuestion: "Add a rating question"
    },
    ScoreFormulaComponentsField: {
        questionColumn: "Question",
        reviewTypeColumn: "Review type",
        weightColumn: "Weight",
        questionPlaceholder: "Select a question",
        reviewTypePlaceholder: "Select",
        weightPlaceholder: "Equal",
        addComponent: "Add input",
        removeComponent: "Remove question",
        weightSum: "Total: {{percent}}%",
        weightSumRequirement: "Weights must add up to 100%.",
        weightAuto: "Auto",
        reviewType: {
            manager: "Manager",
            self: "Self",
            peer: "Peer",
            upward: "Upward"
        }
    },
    CycleReviewTypesField: {
        legend: "Review types",
        lastReviewType: "{{description}} A cycle needs at least one review type, so this one can't be turned off.",
        reviewTypes: {
            self: {
                label: "Self review",
                description: "The reviewee assesses their own work."
            },
            manager: {
                label: "Manager review",
                description: "Their direct manager assesses them."
            },
            peer: {
                label: "Peer review",
                description: "Colleagues they work with assess them."
            },
            upward: {
                label: "Upward review",
                description: "Their reports assess them."
            }
        },
        peer: {
            count: {
                label: "Suggested number of peer reviewers",
                description: "Managers nominate them once the cycle opens."
            }
        },
        upward: {
            nomination: {
                label: "How reviewers are chosen",
                "direct-reports": {
                    label: "All direct reports"
                },
                "reporting-line": {
                    label: "Entire reporting line",
                    description: "Everyone below them, at any depth."
                },
                manual: {
                    label: "Manual",
                    description: "An admin or manager picks them."
                }
            }
        },
        attribution: {
            label: "Author names",
            anonymous: {
                label: "Hidden from the reviewee",
                description: "Managers and executives above them still see who wrote what."
            },
            identified: {
                label: "Shown to the reviewee"
            }
        }
    },
    QuestionnaireBuilder: {
        addQuestion: "Question",
        addSection: "Section",
        outlineLabel: "Questionnaire contents",
        limitReached: "You've reached the {{count}} sections and questions limit.",
        empty: "Add a question to start building the review form.",
        untitledQuestion: "Untitled question",
        untitledSection: "Untitled section",
        reorderSection: "Reorder section",
        reorderQuestion: "Reorder question",
        sectionPosition: "{{name}}, section {{position}} of {{total}}",
        questionPosition: "{{name}}, question {{position}} of {{total}} in {{section}}",
        questionNumber: "Question {{number}}",
        sectionTitlePlaceholder: "Add a section title",
        sectionDescriptionPlaceholder: "Describe what this section is about (optional)",
        questionTitlePlaceholder: "What do you want to ask?",
        questionDescriptionPlaceholder: "Description (optional)",
        duplicateQuestion: "Duplicate question",
        deleteQuestion: "Delete question",
        deleteSection: "Delete section",
        kinds: {
            text: "Open text",
            rating: "Rating scale",
            choice: "Multiple choice"
        },
        answerLabel: "Answer",
        kindLabel: "Answer type",
        textPreview: "Open text answer",
        addChoice: "Add choice",
        choicePlaceholder: "Choice {{number}}",
        deleteChoice: "Delete choice",
        allowMultiple: "Allow the reviewer to pick more than one answer",
        addRatingPoint: "Add point",
        ratingPointPlaceholder: "Label for point {{number}}",
        ratingPointDescriptionLabel: "Description for point {{number}}",
        ratingPointDescriptionPlaceholder: "What this point means (optional)",
        deleteRatingPoint: "Delete scale point",
        audiences: {
            self: "Reviewee",
            manager: "Manager",
            peer: "Peers",
            upward: "Reports"
        },
        responses: {
            self: "Their own responses",
            manager: "Manager responses",
            peer: "Peer responses",
            upward: "Responses from their reports"
        },
        whoAnswers: "Who answers this",
        noReviewTypes: "This cycle runs no reviews yet.",
        required: "Required",
        sharedWithReviewee: "Shared with the reviewee",
        nobodyAsked: "Nobody is asked this question yet.",
        rewordFor: "Reword for",
        rewordingFor: "Wording for {{audience}}",
        useSharedWording: "Use the shared wording for {{audience}}",
        issues: {
            noQuestions: "At least one section and question is required.",
            emptySection: "This section is empty.",
            untitledSection: "This section needs a name.",
            noAudience: "Assign this question to at least one review.",
            untitledQuestion: "This question needs wording.",
            tooFewChoices: "Offer at least {{count}} choices.",
            unlabelledChoice: "A choice has no label.",
            tooFewRatingPoints: "Offer at least {{count}} scale points.",
            unlabelledRatingPoint: "A scale point has no label.",
            tooManySlots: "You've reached the {{count}} sections and questions limit.",
            reviewTypeWithoutRequiredQuestion: "{{audience}} needs at least one required question."
        }
    },
    CycleBuilderFooter: {
        cancel: "Cancel",
        saving: "Saving in progress. Hang tight!",
        saved: "All changes saved",
        saveError: "Changes not saved",
        saveRefused: "Some changes couldn't be saved"
    },
    FeaturePage: {
        workleapGuardrails: {
            title: "Workleap guardrails",
            description: "Built in. Always on.",
            showAll: "Show all {{count}}",
            showFewer: "Show fewer",
            rules: {
                engagement: [
                    "Never attribute a quote or response to a specific person when anonymity was promised.",
                    "When anonymity is compromised by small sample size, suppress the theme rather than the people.",
                    "Never assert a cause for a score change the data cannot support.",
                    "Suppress any sub-group score that falls below the anonymity threshold.",
                    "Never attribute a comment to a person when the feedback was submitted anonymously.",
                    "Don't let one large theme crowd out a smaller but real signal.",
                    "Never promise a commitment the manager has not signed off on.",
                    "Preserve anonymity of the source when the feedback was submitted anonymously.",
                    "Aggregate the signals. Never include individual responses or anything that could de-anonymize a small team.",
                    "Suppress any metric that falls below the anonymity threshold rather than reporting it."
                ],
                performance_reviews: [
                    "Nothing is made up. Every claim traces back to something real, like a goal update, a note, or a piece of feedback, and when the evidence is thin it says so and writes close to nothing rather than inventing a convincing draft.",
                    "It describes, it doesn't decide. It names patterns and lays out the evidence, but never reaches a verdict or recommends a decision. That call is yours.",
                    "It only sees what you're allowed to see. Access follows each review type's permissions, so a peer reviewer can't pull private 1:1 history, for example.",
                    "It surfaces what's hard, not just what's easy. Things like missed commitments or fading engagement get named plainly instead of smoothed over.",
                    "The rating stays yours. It can lay out what the evidence supports, but it never proposes a rating, even under pressure.",
                    "Your words win. It won't overwrite your edits or push a 'better' version over what you wrote.",
                    "Nothing goes out without you. It drafts; you edit and sign off. It never submits on your behalf or acts unprompted."
                ],
                people: [
                    "Nothing is made up. Every statement about a person comes from context available for the request.",
                    "It doesn't assess performance or potential, predict whether someone will leave, or draw conclusions about character, health, or personal circumstances.",
                    "It says when context is missing instead of presenting an incomplete picture as a complete one."
                ]
            }
        }
    }
};
